import { CanopyProvider } from '@bloomfieldrobotics/canopy';
import { useTheme } from '@mui/material/styles';
import { AnimatePresence, MotionStyle, motion } from 'framer-motion';
import { useMemo } from 'react';
import { useToggle } from 'react-use';
import { useAppSelector } from '../../../../app/hooks';
import { selectCurrBlockID } from '../../../select/selectSlice';
import {
  RanchAreaEnum,
  RanchPageViewEnum,
  selectRanchFocusArea,
  selectRanchPageView,
} from '../../ranchesSlice';
import RanchAreaFullscreenToggle from '../RanchAreaFullscreenToggle';
import RanchAreaHeader_Deprecated from '../RanchAreaHeader_Deprecated';
import { RanchWidgetDialog } from './RanchWidgetDialog';
import { RanchWidgetHeader } from './RanchWidgetHeader';
import { RanchWidgetAction } from './types';

interface Props {
  children: React.ReactNode;
  gridArea: RanchAreaEnum;
  displayHeader?: boolean;
  overflowHidden?: boolean;
  /**
   * If true, the fullscreen toggle button will be displayed
   */
  displayFullscreenToggle?: boolean;
  minHeight?: string;
  style?: MotionStyle;
}

// Areas that are visible when in ranch view
const visibleAreasInRanchView = [RanchAreaEnum.mapArea];

// Areas that are visible when in block view
const visibleAreasInBlockView = [
  RanchAreaEnum.mapArea,
  RanchAreaEnum.imageArea,
  RanchAreaEnum.blockInfoArea,
  RanchAreaEnum.chartArea,
  RanchAreaEnum.timelineArea,
];

const visibleAreasInScanView = [RanchAreaEnum.scansArea];

const RanchWidget: React.FC<Props> = ({
  children,
  gridArea,
  displayHeader = true,
  overflowHidden = false,
  displayFullscreenToggle = false,
  minHeight = 'unset',
  style,
}) => {
  const theme = useTheme();
  const [isDialogOpen, toggleDialogOpen] = useToggle(false);

  // Get a list of the current visible areas
  const focusedArea = useAppSelector(selectRanchFocusArea);
  const currBlockID = useAppSelector(selectCurrBlockID);
  const ranchPageView = useAppSelector(selectRanchPageView);
  // If currBlockID is empty, then we are in ranch view, else in block view
  const currVisibleAreas = useMemo(() => {
    if (ranchPageView === RanchPageViewEnum.scansPage) {
      return visibleAreasInScanView;
    }
    if (ranchPageView === RanchPageViewEnum.ranchSummary) {
      return visibleAreasInRanchView;
    }
    return visibleAreasInBlockView;
  }, [ranchPageView, focusedArea, currBlockID]);

  if (
    !(
      (focusedArea === null || focusedArea === gridArea) &&
      currVisibleAreas.includes(gridArea)
    )
  ) {
    return null;
  }

  const handleHeaderAction = (action: RanchWidgetAction) => {
    switch (action) {
      case 'DISPLAY_DIALOG':
        toggleDialogOpen();
        break;
      default:
        console.warn(
          `Unknown string, ${action}, provided for action argument in onHeaderAction`
        );
        break;
    }
  };

  // TODO: When cleaning up this FF, do not use this RanchHeader variable anymore. Replace its reference
  // with the RanchWidgetHeader component directly. Also, do not carry over the gridArea equality part
  // of the conditional here. Its a hack that helps not have to completely rewrite the whole Ranches/Ranch Area
  // UI for Blueberry Sizing. However, we absolutely need to rewrite this before we add any more features or
  // extensive changes. This code as it is now is not easily scalable.
  const RanchHeader =
    gridArea === RanchAreaEnum.chartArea ? (
      <RanchWidgetHeader type={gridArea} onHeaderAction={handleHeaderAction} />
    ) : (
      <RanchAreaHeader_Deprecated gridArea={gridArea} />
    );

  return (
    <>
      <AnimatePresence>
        <motion.div
          layout
          style={{
            gridArea: gridArea,
            width: '100%',
            height: '100%',
            overflow: overflowHidden ? 'hidden' : 'unset', // used to apply border radius to children
            borderRadius: theme.borderRadius.primary,
            boxShadow: 'none',
            backgroundColor: theme.colors.darkTheme.grey.light,
            display: 'grid',
            gridTemplateColumns: '1fr',
            gridTemplateRows: displayHeader ? 'min-content 1fr' : '1fr',
            position: 'relative',
            minHeight: minHeight,
            ...style,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {displayHeader && RanchHeader}
          {displayFullscreenToggle && (
            <RanchAreaFullscreenToggle gridArea={gridArea} />
          )}
          {children}
        </motion.div>
      </AnimatePresence>
      {displayHeader && (
        <CanopyProvider>
          <RanchWidgetDialog
            isOpen={isDialogOpen}
            children={children}
            onClose={() => toggleDialogOpen(false)}
          />
        </CanopyProvider>
      )}
    </>
  );
};

export default RanchWidget;
